import { SubmitEvent, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { zodResolver } from "@hookform/resolvers/zod"
import { LoadingSpinner } from "@/components/loading-spinner"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContentCustom, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { BetterDocPreferredCareProviderResult } from "@/modules/patients/detail/better-doc/de/components/better-doc-preferred-care-provider/better-doc-preferred-care-provider-result"
import {
  BetterDocPreferredCareProviderSearchFormSchema,
  betterDocPreferredCareProviderSearchSchema,
} from "@/modules/patients/detail/better-doc/de/schema/better-doc-preferred-care-provider-search-schema"
import {
  BetterDocCareProviderSearchItem,
  BetterDocCareProviderSearchResult,
  fetchBetterDocCareProviderSearch,
} from "@/modules/patients/detail/better-doc/de/services/betterDocCareProviderSearchQuery"
import type { PointOfCareType } from "@/types/__generated__/union-types"

type PreferredCareProviderSearchOutcome =
  | { kind: "idle" }
  | { kind: "searching" }
  | { kind: "found"; careProviders: ReadonlyArray<BetterDocCareProviderSearchItem> }
  | { kind: "nothingFound" }
  | { kind: "unsupportedSpecialty" }
  | { kind: "failed" }

const getSearchOutcome = (
  searchResult: BetterDocCareProviderSearchResult | undefined,
): PreferredCareProviderSearchOutcome => {
  if (searchResult?.status === "SUCCESS") {
    return searchResult.careProviders.length > 0
      ? { kind: "found", careProviders: searchResult.careProviders }
      : { kind: "nothingFound" }
  }
  if (searchResult?.status === "INVALID_SPECIALTY") {
    return { kind: "unsupportedSpecialty" }
  }
  return { kind: "failed" }
}

const nullWhenBlank = (searchText: string | undefined) => searchText || null

type BetterDocPreferredCareProviderSearchDialogProps = {
  progressNoteId: string
  effectivePointOfCare: PointOfCareType
  specialtyId: string | null
  onSelect: (careProvider: BetterDocCareProviderSearchItem) => void
  onClose: () => void
}

export const BetterDocPreferredCareProviderSearchDialog = ({
  progressNoteId,
  effectivePointOfCare,
  specialtyId,
  onSelect,
  onClose,
}: BetterDocPreferredCareProviderSearchDialogProps) => {
  const { t } = useTranslation()
  const [searchOutcome, setSearchOutcome] = useState<PreferredCareProviderSearchOutcome>({ kind: "idle" })
  const latestSearchRequestNumber = useRef(0)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<BetterDocPreferredCareProviderSearchFormSchema>({
    resolver: zodResolver(betterDocPreferredCareProviderSearchSchema),
    defaultValues: { name: "", city: "", street: "" },
  })

  const runSearch = async ({ name, city, street }: BetterDocPreferredCareProviderSearchFormSchema) => {
    latestSearchRequestNumber.current += 1
    const searchRequestNumber = latestSearchRequestNumber.current
    setSearchOutcome({ kind: "searching" })

    try {
      const searchResult = await fetchBetterDocCareProviderSearch({
        progressNoteId,
        effectivePointOfCare,
        specialtyId,
        name,
        city: nullWhenBlank(city),
        street: nullWhenBlank(street),
      })
      if (searchRequestNumber === latestSearchRequestNumber.current) {
        setSearchOutcome(getSearchOutcome(searchResult))
      }
    } catch {
      if (searchRequestNumber === latestSearchRequestNumber.current) {
        setSearchOutcome({ kind: "failed" })
      }
    }
  }

  const handleFormSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.stopPropagation()
    handleSubmit(runSearch)(event)
  }

  const handleSelect = (careProvider: BetterDocCareProviderSearchItem) => {
    onSelect(careProvider)
    onClose()
  }

  const renderSearchOutcome = () => {
    switch (searchOutcome.kind) {
      case "idle":
        return null
      case "searching":
        return <LoadingSpinner size={24} className="flex justify-center p-8" />
      case "found":
        return (
          <BetterDocPreferredCareProviderResult careProviders={searchOutcome.careProviders} onSelect={handleSelect} />
        )
      case "nothingFound":
        return (
          <div
            className="text-on-surface-subdued py-8 text-center text-sm"
            data-testid="better-doc-preferred-care-provider-nothing-found">
            {t("common.nothingFound")}
          </div>
        )
      case "unsupportedSpecialty":
        return (
          <Banner
            type="inline"
            variant="warning"
            data-testid="better-doc-preferred-care-provider-unsupported-specialty">
            {t("episodeOfCare.betterDoc.notSupportSpecialty")}
          </Banner>
        )
      case "failed":
        return (
          <Banner type="inline" variant="critical" data-testid="better-doc-preferred-care-provider-search-failed">
            {t("episodeOfCare.betterDoc.preferredCareProvider.searchFailed")}
          </Banner>
        )
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContentCustom
        variant="small"
        onPointerDownOutside={(event) => event.preventDefault()}
        data-testid="better-doc-preferred-care-provider-search-dialog"
        className="flex max-h-[calc(100vh-120px)] flex-col">
        <DialogHeader>
          <DialogTitle>{t("episodeOfCare.betterDoc.preferredCareProvider.searchDialogTitle")}</DialogTitle>
        </DialogHeader>
        <form className="grid gap-4 px-6 pt-2 pb-4" onSubmit={handleFormSubmit}>
          <Input
            {...register("name")}
            label={t("common.name")}
            required
            error={errors.name?.message}
            translateError
            testId="better-doc-preferred-care-provider-name"
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              {...register("city")}
              label={t("common.city")}
              error={errors.city?.message}
              translateError
              testId="better-doc-preferred-care-provider-city"
            />
            <Input
              {...register("street")}
              label={t("common.street")}
              error={errors.street?.message}
              translateError
              testId="better-doc-preferred-care-provider-street"
            />
          </div>
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={searchOutcome.kind === "searching"}
              data-testid="better-doc-preferred-care-provider-search-button">
              {t("common.search")}
            </Button>
          </div>
        </form>
        <div className="flex min-h-0 flex-col px-6 pb-6">{renderSearchOutcome()}</div>
      </DialogContentCustom>
    </Dialog>
  )
}
